import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GasVideoPlayer } from '../../shared/kit/components/GasVideoPlayer';
import {
    GasVideoTranscript,
    type GasTranscriptCue,
    type GasTranscriptWord,
} from '../../shared/kit/components/GasVideoTranscript';
import { GasSection, type GasVideoCuePoint, type GasVideoTrackingEvent } from '../../shared/kit';
import { translate } from '../../shared/kit/hooks/useGasI18n';
import { createVideoStatsApi, createQuizApi, type VideoTrackPayload } from './api';
import { QuizCueOverlay } from './QuizCueOverlay';
import type { VideoPlayerInitData, QuizCueData, QuizAnswer, QuizCheckResult } from './types';

interface AppProps {
    initData: VideoPlayerInitData;
}

const PROGRESS_INTERVAL_SECONDS = 30;

export default function App({ initData }: AppProps) {
    const api = useMemo(
        () => createVideoStatsApi(initData.rest.url, initData.rest.nonce, {
            dbVideoId: initData.dbVideoId,
            contentId: initData.contentId,
        }),
        [initData.rest.url, initData.rest.nonce, initData.dbVideoId, initData.contentId],
    );

    const quizApi = useMemo(
        () => createQuizApi(initData.rest.quizUrl, initData.rest.nonce),
        [initData.rest.quizUrl, initData.rest.nonce],
    );

    const [startTime, setStartTime] = useState(0);
    const [resumeResolved, setResumeResolved] = useState(false);
    const [activeCue, setActiveCue] = useState<GasVideoCuePoint | null>(null);
    const [previewBlocked, setPreviewBlocked] = useState(false);
    const lastProgressSentRef = useRef(0);
    const mediaRef = useRef<HTMLVideoElement | null>(null);

    // Transcript interactif (drawer synchronisé).
    const [cues, setCues] = useState<GasTranscriptCue[]>([]);
    const [words, setWords] = useState<GasTranscriptWord[]>([]);
    const [currentTime, setCurrentTime] = useState(0);
    const [seekRequest, setSeekRequest] = useState<{ time: number; nonce: number } | undefined>(undefined);

    useEffect(() => {
        if (!initData.showTranscript) {
            return;
        }
        let cancelled = false;
        const base = initData.rest.url.replace(/\/$/, '');
        const params = new URLSearchParams();
        if (initData.dbVideoId) params.set('db_video_id', String(initData.dbVideoId));
        if (initData.contentId) params.set('content_id', String(initData.contentId));
        params.set('lang', 'fr');
        fetch(`${base}/transcript?${params.toString()}`, {
            headers: initData.rest.nonce ? { 'X-WP-Nonce': initData.rest.nonce } : undefined,
        })
            .then((res) => (res.ok ? res.json() : null))
            .then((json) => {
                if (cancelled || !json?.data) return;
                setCues((json.data.cues || []) as GasTranscriptCue[]);
                setWords((json.data.words || []) as GasTranscriptWord[]);
            })
            .catch(() => undefined);
        return () => { cancelled = true; };
    }, [initData.showTranscript, initData.rest.url, initData.rest.nonce, initData.dbVideoId, initData.contentId]);

    const handleSeek = useCallback((seconds: number) => {
        setSeekRequest((prev) => ({ time: seconds, nonce: (prev?.nonce ?? 0) + 1 }));
    }, []);

    // Deep-link temporel : `?t=90` (optionnellement scopé `?content_video={id}`,
    // format produit par les chunks de recherche). Prioritaire sur la reprise.
    const deepLinkSeconds = useMemo(() => {
        const params = new URLSearchParams(window.location.search);
        const target = parseInt(params.get('content_video') || '0', 10);
        if (target > 0 && initData.contentId > 0 && target !== initData.contentId) {
            return null;
        }
        const t = parseInt(params.get('t') || '', 10);
        return !Number.isNaN(t) && t >= 0 ? t : null;
    }, [initData.contentId]);

    // Seek piloté depuis l'extérieur de l'arbre React (artifacts `highlight`
    // rendus en PHP, liens de chapitres, …) via CustomEvent `gas:video-seek`.
    useEffect(() => {
        function onExternalSeek(event: Event) {
            const detail = (event as CustomEvent<{ seconds?: number; contentId?: number }>).detail;
            if (!detail || typeof detail.seconds !== 'number' || detail.seconds < 0) return;
            if (detail.contentId && initData.contentId > 0 && detail.contentId !== initData.contentId) return;
            handleSeek(detail.seconds);
        }
        window.addEventListener('gas:video-seek', onExternalSeek);
        return () => window.removeEventListener('gas:video-seek', onExternalSeek);
    }, [handleSeek, initData.contentId]);

    // Accumulation des réponses pour le submit final (flux quiz officiel).
    const collectedAnswersRef = useRef<{ quizId: number; answer: QuizAnswer }[]>([]);
    const submittedQuizRef = useRef<Set<number>>(new Set());

    useEffect(() => {
        // Un deep-link `?t=` explicite gagne sur la reprise de lecture.
        if (deepLinkSeconds !== null) {
            setStartTime(deepLinkSeconds);
            setResumeResolved(true);
            return undefined;
        }
        let cancelled = false;
        api.getResume().then((position) => {
            if (cancelled) return;
            if (position && position > 0) setStartTime(position);
            setResumeResolved(true);
        });
        return () => { cancelled = true; };
    }, [api, deepLinkSeconds]);

    function toPayload(event: GasVideoTrackingEvent): VideoTrackPayload {
        return {
            db_video_id: initData.dbVideoId,
            content_id: initData.contentId,
            activite_id: initData.activiteId,
            event_type: event.eventType,
            current_time: event.currentTime,
            duration_watched: 0,
            video_duration: event.duration,
            chapter_index: event.chapter?.index ?? null,
            chapter_title: event.chapter?.title ?? null,
            provider: initData.provider,
        };
    }

    function handleTrackEvent(event: GasVideoTrackingEvent) {
        if (event.eventType === 'play') {
            api.trackEvent(toPayload(event));
            return;
        }

        if (event.eventType === 'complete') {
            api.trackEvent(toPayload(event));
            // Submit officiel du quiz en fin de vidéo (une seule fois par quiz_id).
            const byQuiz = new Map<number, QuizAnswer[]>();
            for (const { quizId, answer } of collectedAnswersRef.current) {
                if (!byQuiz.has(quizId)) byQuiz.set(quizId, []);
                byQuiz.get(quizId)!.push(answer);
            }
            for (const [quizId, answers] of byQuiz) {
                if (submittedQuizRef.current.has(quizId)) continue;
                submittedQuizRef.current.add(quizId);
                quizApi.submitQuiz(quizId, answers, initData.contentId, initData.rest.quizNonce);
            }
            return;
        }

        if (
            event.currentTime - lastProgressSentRef.current >= PROGRESS_INTERVAL_SECONDS
            || event.eventType === 'pause'
            || event.eventType === 'seek'
        ) {
            lastProgressSentRef.current = event.currentTime;
            api.trackEvent({ ...toPayload(event), event_type: 'progress', duration_watched: PROGRESS_INTERVAL_SECONDS });
        }
    }

    const handleCuePoint = useCallback((cue: GasVideoCuePoint) => {
        setActiveCue(cue);
    }, []);

    function handleQuizAnswered(quizId: number, answer: QuizAnswer, _result: QuizCheckResult | null) {
        collectedAnswersRef.current.push({ quizId, answer });
    }

    function handleCueClose() {
        setActiveCue(null);
        // Reprendre la lecture (le player est déjà en pause côté GasVideoPlayer).
        if (mediaRef.current && !mediaRef.current.paused) return;
        mediaRef.current?.play().catch(() => undefined);
    }

    if (!resumeResolved) return null;

    const isCueQuiz = activeCue !== null && typeof activeCue.data === 'object' && activeCue.data !== null
        && 'quiz_group_id' in (activeCue.data as object);
    const cueQuizData = isCueQuiz ? (activeCue!.data as unknown as QuizCueData) : null;

    const previewCtaUrl = initData.previewCtaUrl || '';

    return (
        <div className="gas-video-player-host">
            <GasVideoPlayer
                src={initData.videoUrl}
                title={initData.title}
                poster={initData.poster}
                mediaType={initData.isAudio ? 'audio' : 'video'}
                provider={initData.provider}
                videoId={initData.videoId}
                dbVideoId={initData.dbVideoId}
                contentId={initData.contentId}
                activiteId={initData.activiteId}
                isHls={initData.isHls}
                autoplay={initData.autoplay}
                startTime={startTime}
                tracks={initData.subtitles}
                chapters={initData.chapters}
                cuePoints={initData.cuePoints || []}
                previewSeconds={initData.previewSeconds || 0}
                overlays={initData.overlays || []}
                seekRequest={seekRequest}
                captionCues={cues}
                captionsDefaultOn={initData.captionsDefaultOn ?? false}
                subtitleStyle={initData.subtitleStyle}
                showAbLoop={initData.abLoop ?? false}
                onTrackEvent={handleTrackEvent}
                onTimeUpdate={(t) => setCurrentTime(t)}
                onCuePoint={handleCuePoint}
                onPreviewLimit={() => setPreviewBlocked(true)}
            />

            {/* Transcript interactif synchronisé (drawer repliable) — single activité. */}
            {initData.showTranscript && cues.length > 0 ? (
                <GasSection
                    title={translate('video.transcript.title')}
                    variant="card"
                    collapsible
                    defaultExpanded
                    persistKey="video_transcript"
                >
                    <GasVideoTranscript
                        cues={cues}
                        words={words}
                        currentTime={currentTime}
                        onSeek={handleSeek}
                        className="gas-video-player-host__transcript gas-video-transcript--bare"
                    />
                </GasSection>
            ) : null}

            {/* Point d'arrêt e-learning avec quiz branché */}
            {activeCue && cueQuizData ? (
                <QuizCueOverlay
                    cueData={cueQuizData}
                    api={quizApi}
                    onAnswered={handleQuizAnswered}
                    onClose={handleCueClose}
                />
            ) : activeCue ? (
                // Overlay générique pour les cues sans quiz (label seul)
                <div className="gas-video-player-host__overlay" role="dialog" aria-modal="true">
                    <div className="gas-video-player-host__panel">
                        <p>{activeCue.label || translate('video.player.cue_default')}</p>
                        <button
                            type="button"
                            className="gas-button gas-button--primary"
                            onClick={handleCueClose}
                        >
                            {translate('common.continue')}
                        </button>
                    </div>
                </div>
            ) : null}

            {/* Limite teaser/extrait atteinte */}
            {previewBlocked ? (
                <div className="gas-video-player-host__overlay" role="dialog" aria-modal="true">
                    <div className="gas-video-player-host__panel">
                        <p>{translate('video.player.preview_limit')}</p>
                        {previewCtaUrl ? (
                            <a className="gas-button gas-button--primary" href={previewCtaUrl}>
                                {translate('video.player.preview_cta')}
                            </a>
                        ) : null}
                    </div>
                </div>
            ) : null}
        </div>
    );
}
